import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import {
  useUpdateWarehouseNotificationConfig,
  useWarehouseNotificationConfig,
} from '../hooks/useWarehouseNotificationConfig';
import {
  addGroupMembershipVariables,
  addJobRecipientVariables,
  previewRecipientsVariables,
  removeGroupMembershipVariables,
  removeJobRecipientVariables,
  updateGroupSettingsVariables,
  useAddGroupMembership,
  useAddJobRecipient,
  useAssignableUsers,
  useOpenJobs,
  usePreviewRecipients,
  useRemoveGroupMembership,
  useRemoveJobRecipient,
  useUpdateGroupSettings,
  useWarehouseDepartmentGroups,
} from '../hooks/useWarehouseDepartmentGroups';
import {
  AssignableUser,
  GroupNotificationSettings,
  JobRecipient,
  NOTIFICATION_ROLES,
  NotificationMode,
  NotificationRoleKey,
  OpenJob,
  RecipientPreview,
  WarehouseDepartmentGroup,
  WarehouseNotificationConfig,
  warehouseDepartmentGroupsUrl,
  warehouseNotificationConfigUrl,
} from '../model';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const assignableUserLabel = (user: AssignableUser): string => `${user.full_name} (${user.email})`;

const departmentGroupDisplayName = (groupId: string): string =>
  groupId === 'ALL_DEPARTMENTS' ? 'All Departments' : groupId;

const previewGroupLabel = (groups: string[]): string => {
  const names = groups.map(departmentGroupDisplayName);
  if (names.length === 0) return 'no group';
  const noun = names.length === 1 ? 'group' : 'groups';
  return `${noun} ${names.join(', ')}`;
};

const activeRoleCount = (group: WarehouseDepartmentGroup): number =>
  NOTIFICATION_ROLES.filter((role) => group.settings.roles[role.key]).length;

const filterAssignableUsers = (options: AssignableUser[], inputValue: string): AssignableUser[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((user) => assignableUserLabel(user).toLowerCase().includes(query));
};

const sectionTitleSx = {
  color: 'primary.main',
  fontWeight: 600,
  fontSize: '14px',
} as const;

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'visible',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    borderColor: 'primary.light',
    bgcolor: 'grey.50',
  },
} as const;

const settingsBoxSx = {
  p: 2,
  mb: 2,
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'primary.light',
  bgcolor: 'rgba(25, 118, 210, 0.04)',
} as const;

interface JobRecipientsSectionProps {
  group: WarehouseDepartmentGroup;
  location: string;
  expanded: boolean;
  assignableUsers: AssignableUser[];
  onAdd: (groupId: string, jobId: string, user: AssignableUser) => void;
  onRemove: (recipient: JobRecipient, groupId: string) => void;
  busyKey: string | null;
}

const jobLabel = (job: OpenJob): string => `${job.job_id} — ${job.description ?? ''}`.trim();

const JobRecipientsSection: React.FC<JobRecipientsSectionProps> = ({
  group,
  location,
  expanded,
  assignableUsers,
  onAdd,
  onRemove,
  busyKey,
}) => {
  const [job, setJob] = useState<OpenJob | null>(null);
  const [user, setUser] = useState<AssignableUser | null>(null);
  // Only fetched once the accordion is open: a group carries a few hundred open jobs, and a
  // collapsed page has no reason to pull every group's list.
  const { data: jobsResponse, isLoading } = useOpenJobs(group.group_id, location, expanded);
  const openJobs: OpenJob[] = jobsResponse?.data ?? [];

  const handleAdd = () => {
    if (!job || !user) return;
    onAdd(group.group_id, job.job_id, user);
    setJob(null);
    setUser(null);
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Job-specific recipients
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Notified only when a receiving comes in against their job, whatever the mode above. Open jobs only.
      </Typography>
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Notify</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {group.job_recipients.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  No job-specific recipients.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {group.job_recipients.map((recipient) => (
            <TableRow key={recipient.id}>
              <TableCell>{recipient.job_id}</TableCell>
              <TableCell>{recipient.job_desc ?? '—'}</TableCell>
              <TableCell>{recipient.user?.full_name}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  disabled={busyKey === `${group.group_id}:job:${recipient.id}`}
                  onClick={() => onRemove(recipient, group.group_id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Autocomplete
          options={openJobs}
          loading={isLoading}
          getOptionLabel={jobLabel}
          isOptionEqualToValue={(option, value) => value != null && option.job_id === value.job_id}
          value={job}
          onChange={(_event, value) => setJob(value)}
          ListboxProps={{ style: { maxHeight: 280 } }}
          componentsProps={{ popper: { placement: 'bottom-start', style: { zIndex: 1500 } } }}
          renderInput={(params) => <TextField {...params} label="Open job" size="small" autoComplete="off" />}
          sx={{ minWidth: 260, flex: 1 }}
        />
        <Autocomplete
          options={assignableUsers}
          getOptionLabel={assignableUserLabel}
          isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
          value={user}
          onChange={(_event, value) => setUser(value)}
          ListboxProps={{ style: { maxHeight: 280 } }}
          componentsProps={{ popper: { placement: 'bottom-start', style: { zIndex: 1500 } } }}
          renderInput={(params) => <TextField {...params} label="User" size="small" autoComplete="off" />}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <Button variant="outlined" size="small" disabled={!job || !user} onClick={handleAdd}>
          Add
        </Button>
      </Box>
    </>
  );
};

const WarehouseNotificationConfigPage: React.FC = () => {
  const [location] = useState(() => getWarehouseSelectedLocation());
  const configUrl = warehouseNotificationConfigUrl(location);
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useWarehouseNotificationConfig(location);
  const { mutateAsync, isPending } = useUpdateWarehouseNotificationConfig();
  const { data: groupsResponse, isLoading: groupsLoading } = useWarehouseDepartmentGroups(location);
  const { data: usersResponse } = useAssignableUsers(location);
  const addMembership = useAddGroupMembership();
  const removeMembership = useRemoveGroupMembership();
  const updateSettings = useUpdateGroupSettings();
  const addJobRecipient = useAddJobRecipient();
  const removeJobRecipient = useRemoveJobRecipient();

  const [config, setConfig] = useState<WarehouseNotificationConfig | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pickerKeys, setPickerKeys] = useState<Record<string, number>>({});
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [previewPo, setPreviewPo] = useState('');
  const [preview, setPreview] = useState<RecipientPreview | null>(null);
  const previewRecipients = usePreviewRecipients();

  useEffect(() => {
    const result = data?.data;
    if (result) setConfig(result);
  }, [data?.data]);

  const handleCopyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(variable);
      showSuccessSnackBar?.(`Copied ${variable}`);
    } catch {
      showErrorSnackBar?.('Failed to copy to clipboard.');
    }
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      const response = await mutateAsync({
        url: configUrl,
        data: { config: { subject: config.subject, body: config.body } },
      });
      const saved = response.data ?? config;
      setConfig(saved);
      showSuccessSnackBar?.('Configuration saved.');
    } catch {
      showErrorSnackBar?.('Failed to save configuration.');
    }
  };

  const bumpPicker = (groupId: string) => {
    setPickerKeys((prev) => ({ ...prev, [groupId]: (prev[groupId] ?? 0) + 1 }));
  };

  const groups: WarehouseDepartmentGroup[] = groupsResponse?.data ?? [];
  const assignableUsers: AssignableUser[] = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [warehouseDepartmentGroupsUrl, { location }] });
  };

  const handleAdd = async (groupId: string, user: AssignableUser | null) => {
    if (!user) return;
    setBusyKey(`${groupId}:${user.id}`);
    try {
      await addMembership.mutateAsync(addGroupMembershipVariables(user.id, groupId, location));
      await refresh();
      bumpPicker(groupId);
      showSuccessSnackBar?.(`Added ${user.full_name} to ${departmentGroupDisplayName(groupId)}`);
    } catch {
      showErrorSnackBar?.('Could not add user to department');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (membershipId: number, groupId: string, fullName: string) => {
    setBusyKey(`${groupId}:${membershipId}`);
    try {
      await removeMembership.mutateAsync(removeGroupMembershipVariables(membershipId, location));
      await refresh();
      showSuccessSnackBar?.(`Removed ${fullName} from ${departmentGroupDisplayName(groupId)}`);
    } catch {
      showErrorSnackBar?.('Could not remove user from department');
    } finally {
      setBusyKey(null);
    }
  };

  // Saved on toggle rather than behind a Save button: adding and removing a user on this page
  // already commits immediately, so a button here would be the only dirty state to manage.
  const handleSettingsChange = async (groupId: string, settings: GroupNotificationSettings) => {
    if (busyKey === `${groupId}:settings`) return;
    setBusyKey(`${groupId}:settings`);
    try {
      await updateSettings.mutateAsync(updateGroupSettingsVariables(groupId, location, settings));
      await refresh();
    } catch {
      showErrorSnackBar?.('Could not save notification settings');
    } finally {
      setBusyKey(null);
    }
  };

  const handleAddJobRecipient = async (groupId: string, jobId: string, user: AssignableUser) => {
    setBusyKey(`${groupId}:job:add`);
    try {
      await addJobRecipient.mutateAsync(addJobRecipientVariables(groupId, jobId, user.id, location));
      await refresh();
      showSuccessSnackBar?.(`${user.full_name} will be notified about job ${jobId}`);
    } catch {
      showErrorSnackBar?.('Could not add job recipient');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemoveJobRecipient = async (recipient: JobRecipient, groupId: string) => {
    setBusyKey(`${groupId}:job:${recipient.id}`);
    try {
      await removeJobRecipient.mutateAsync(removeJobRecipientVariables(recipient.id, location));
      await refresh();
      showSuccessSnackBar?.(`Removed ${recipient.user?.full_name} from job ${recipient.job_id}`);
    } catch {
      showErrorSnackBar?.('Could not remove job recipient');
    } finally {
      setBusyKey(null);
    }
  };

  const handlePreview = async () => {
    if (!previewPo.trim()) return;
    setPreview(null);
    try {
      const response = await previewRecipients.mutateAsync(
        previewRecipientsVariables(previewPo.trim(), location),
      );
      setPreview(response.data ?? null);
    } catch {
      showErrorSnackBar?.('Could not resolve recipients for that PO');
    }
  };

  const handleModeChange = (group: WarehouseDepartmentGroup, mode: NotificationMode) =>
    handleSettingsChange(group.group_id, { ...group.settings, mode });

  const handleRoleToggle = (group: WarehouseDepartmentGroup, key: NotificationRoleKey) =>
    handleSettingsChange(group.group_id, {
      ...group.settings,
      roles: { ...group.settings.roles, [key]: !group.settings.roles[key] },
    });

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Warehouse — Notifications" titleIcon={<NotificationsIcon />} />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        {isLoading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {isError && <ErrorMessage message="Failed to load configuration." />}
        {!isLoading && !isError && config && (
          <LoadingWrapper isLoading={isPending} message="Saving...">
            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}>
                <Typography variant="h6">Email Template</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, pt: 0 }}>
              <Box>
                <TextField
                  label="Email Subject"
                  fullWidth
                  value={config.subject}
                  onChange={(e) => setConfig({ ...config, subject: e.target.value })}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Template Variables — click to copy, then paste into the subject or body
                  </Typography>
                  {(config.template_variables ?? []).map((variable) => (
                    <Chip
                      key={variable}
                      icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      label={variable}
                      onClick={() => handleCopyVariable(variable)}
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Email Body
                </Typography>
                {/* height sets the container; marginBottom prevents the Quill toolbar from overlapping elements below */}
                <ReactQuill
                  placeholder="Enter email body here..."
                  theme="snow"
                  value={config.body}
                  onChange={(value) => setConfig({ ...config, body: value })}
                  modules={QUILL_MODULES}
                  style={{ height: 200, marginBottom: 48 }}
                />

                <Box>
                  <Button variant="contained" color="primary" onClick={handleSave}>
                    Save Configuration
                  </Button>
                </Box>
              </Box>
              </AccordionDetails>
            </Accordion>
          </LoadingWrapper>
        )}
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" sx={sectionTitleSx} gutterBottom>
          Departments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Expand a department group to set who is notified: the people you assign, the roles pulled from the PO and
          its job at send time, and any job-specific recipients.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: 'primary.light', opacity: 0.5 }} />

        {/* A real PO resolves its own department, which resolves its own groups - so this
            answers for the whole page rather than for whichever accordion happens to be open. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Who would be notified?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="PO number"
              size="small"
              value={previewPo}
              onChange={(e) => setPreviewPo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePreview()}
              sx={{ maxWidth: 200 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!previewPo.trim() || previewRecipients.isPending}
              startIcon={previewRecipients.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={handlePreview}
            >
              Check
            </Button>
          </Box>
          {preview?.error && <ErrorMessage message={preview.error} />}
          {preview && !preview.error && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                PO {preview.po_number} — {preview.department ?? 'no department'} — {previewGroupLabel(preview.groups)}
                {preview.job_id ? ` — job ${preview.job_id}` : ' — no job'}
              </Typography>
              {preview.recipients.length === 0 ? (
                <Typography variant="body2" color="error">
                  Nobody would be emailed for this PO.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {preview.recipients.map((recipient) => (
                    <Chip
                      key={recipient.id}
                      size="small"
                      variant="outlined"
                      label={`${recipient.full_name} · ${recipient.reasons.join(', ')}`}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {groupsLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
            {groups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No departments for this location.
              </Typography>
            )}
            <Grid container columnSpacing={1.5} rowSpacing={0.5}>
              {groups.map((group) => {
                const settingsBusy = busyKey === `${group.group_id}:settings`;
                const memberIds = new Set(group.users.map((u) => u.id));
                const options = assignableUsers.filter((u) => !memberIds.has(u.id));
                return (
                  <Grid item xs={12} md={6} key={group.group_id}>
                    <Accordion
                      disableGutters
                      sx={accordionSx}
                      expanded={expandedGroup === group.group_id}
                      onChange={(_e, isExpanded) => setExpandedGroup(isExpanded ? group.group_id : null)}
                      // Only one group can be open, and MUI mounts children eagerly - without this
                      // every collapsed group keeps a Table and two Autocompletes mounted.
                      TransitionProps={{ unmountOnExit: true }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                        sx={{
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, mr: 0.5 }}>
                          {departmentGroupDisplayName(group.group_id)}
                        </Typography>
                        {activeRoleCount(group) > 0 && (
                          <Chip
                            size="small"
                            color={group.settings.mode === 'supersede' ? 'warning' : 'primary'}
                            variant="outlined"
                            label={
                              group.settings.mode === 'supersede'
                                ? `${activeRoleCount(group)} roles — instead of`
                                : `${activeRoleCount(group)} roles — in addition`
                            }
                          />
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 'auto', mr: 1, whiteSpace: 'nowrap' }}
                        >
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {group.users.length}
                          </Box>{' '}
                          users
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ overflow: 'visible', pt: 0 }}>
                        <Box sx={settingsBoxSx}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Dynamic recipients
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            People found from the PO and its job when a receiving is submitted.
                          </Typography>

                          <RadioGroup
                            row
                            value={group.settings.mode}
                            onChange={(e) => handleModeChange(group, e.target.value as NotificationMode)}
                            sx={{ mt: 1 }}
                          >
                            <FormControlLabel
                              value="additive"
                              control={<Radio size="small" />}
                              disabled={settingsBusy}
                              label={<Typography variant="body2">In addition to assigned users</Typography>}
                            />
                            <FormControlLabel
                              value="supersede"
                              control={<Radio size="small" />}
                              disabled={settingsBusy}
                              label={<Typography variant="body2">Instead of assigned users</Typography>}
                            />
                          </RadioGroup>

                          <FormGroup sx={{ mt: 0.5 }}>
                            <Grid container>
                              {NOTIFICATION_ROLES.map((role) => (
                                <Grid item xs={12} sm={6} key={role.key}>
                                  <Tooltip title={`ServMan field: ${role.field}`} placement="right">
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={!!group.settings.roles[role.key]}
                                          disabled={settingsBusy}
                                          onChange={() => handleRoleToggle(group, role.key)}
                                        />
                                      }
                                      label={<Typography variant="body2">{role.label}</Typography>}
                                    />
                                  </Tooltip>
                                </Grid>
                              ))}
                            </Grid>
                          </FormGroup>

                          {group.settings.mode === 'supersede' && (
                            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                              Assigned users in this group will not be emailed. Dynamic roles and job-specific
                              recipients still apply; other groups are unchanged.
                            </Alert>
                          )}
                        </Box>
                        <List dense disablePadding>
                          {group.users.map((user) => (
                            <ListItem
                              key={user.membership_id}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                bgcolor: 'grey.50',
                                borderLeft: '2px solid',
                                borderLeftColor: 'primary.light',
                                opacity: group.settings.mode === 'supersede' ? 0.5 : 1,
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  size="small"
                                  disabled={busyKey === `${group.group_id}:${user.membership_id}`}
                                  onClick={() => handleRemove(user.membership_id, group.group_id, user.full_name)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={user.full_name}
                                secondary={user.email}
                                primaryTypographyProps={{ fontWeight: 500 }}
                              />
                            </ListItem>
                          ))}
                        </List>

                        <Autocomplete
                          key={`add-user-${group.group_id}-${pickerKeys[group.group_id] ?? 0}`}
                          options={options}
                          getOptionLabel={assignableUserLabel}
                          isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
                          value={null}
                          blurOnSelect
                          filterOptions={(opts, state) => filterAssignableUsers(opts, state.inputValue)}
                          onChange={(_event, value) => handleAdd(group.group_id, value)}
                          ListboxProps={{ style: { maxHeight: 280 } }}
                          componentsProps={{ popper: { placement: 'bottom-start', style: { zIndex: 1500 } } }}
                          renderInput={(params) => (
                            <TextField {...params} label="Add user" size="small" color="primary" autoComplete="off" />
                          )}
                          sx={{ maxWidth: 420, mt: 1, mb: 2 }}
                        />

                        <Divider sx={{ my: 2 }} />

                        <JobRecipientsSection
                          group={group}
                          location={location}
                          expanded={expandedGroup === group.group_id}
                          assignableUsers={assignableUsers}
                          onAdd={handleAddJobRecipient}
                          onRemove={handleRemoveJobRecipient}
                          busyKey={busyKey}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default WarehouseNotificationConfigPage;
